import { OVERDUE_CANCELLATION_SUCCESS_TITLE } from "@/app/(application)/(normal)/account/components/cancelSubscriptionLink/cancelSubscriptionModal/constants";
import { CancellationReason } from "@/app/(application)/(normal)/account/components/cancelSubscriptionLink/cancelSubscriptionModal/steps/CancellationReason";
import { CancellationSuccess } from "@/app/(application)/(normal)/account/components/cancelSubscriptionLink/cancelSubscriptionModal/steps/CancellationSuccess";
import { ConfirmCancellation } from "@/app/(application)/(normal)/account/components/cancelSubscriptionLink/cancelSubscriptionModal/steps/ConfirmCancellation";
import { ConnectSupport } from "@/app/(application)/(normal)/account/components/cancelSubscriptionLink/cancelSubscriptionModal/steps/ConnectSupport";
import { DiscountOffer } from "@/app/(application)/(normal)/account/components/cancelSubscriptionLink/cancelSubscriptionModal/steps/DiscountOffer";
import { FeedbackForm } from "@/app/(application)/(normal)/account/components/cancelSubscriptionLink/cancelSubscriptionModal/steps/FeedbackForm";
import { UserStays } from "@/app/(application)/(normal)/account/components/cancelSubscriptionLink/cancelSubscriptionModal/steps/UserStays";
import { Welcome } from "@/app/(application)/(normal)/account/components/cancelSubscriptionLink/cancelSubscriptionModal/steps/Welcome";
import {
  type CancellationReason as Reason,
  type CancellationStep,
  type OfferedDiscount,
  type UserNoDates,
} from "@/types/types";

export function StepRenderer({
  currentStep,
  user,
  numberOfModels,
  numberOfMixedModels,
  selectedReason,
  offeredDiscount,
  wasCancelledImmediately,
  handleChoice,
  handleClose,
  onCancellationCompleted,
  shuffleArray,
}: {
  currentStep: CancellationStep;
  user: UserNoDates;
  numberOfModels: number;
  numberOfMixedModels: number;
  selectedReason: Reason;
  offeredDiscount: OfferedDiscount | null;
  wasCancelledImmediately: boolean;
  handleChoice: (
    nextStepId: string,
    reason: Reason,
    feedback: string | null
  ) => void;
  handleClose: () => void;
  onCancellationCompleted: (cancelledImmediately: boolean) => void;
  shuffleArray: <T>(array: T[]) => T[];
}) {
  switch (currentStep.id) {
    case "welcome":
      return (
        <Welcome
          title={currentStep.title}
          user={user}
          numberOfModels={numberOfModels}
          numberOfMixedModels={numberOfMixedModels}
          onContinue={() =>
            handleChoice(currentStep.next as string, selectedReason, null)
          }
          onStay={() => handleChoice("user_stays", selectedReason, null)}
        />
      );

    case "ask_cancellation_reason":
      return (
        <CancellationReason
          title={currentStep.title}
          choices={currentStep.choices === null ? [] : currentStep.choices}
          shuffleArray={shuffleArray}
          onChoiceSelected={handleChoice}
        />
      );

    case "show_feedback_form":
      return (
        <FeedbackForm
          title={currentStep.title}
          onSubmit={(feedback) =>
            handleChoice("confirm_cancellation", selectedReason, feedback)
          }
        />
      );

    case "apply_discount":
      return (
        <DiscountOffer
          title={currentStep.title}
          offeredDiscount={offeredDiscount}
          onAccept={() => handleChoice("user_stays", selectedReason, null)}
          onReject={() =>
            handleChoice("confirm_cancellation", selectedReason, null)
          }
        />
      );

    case "connect_support":
      return (
        <ConnectSupport
          title={currentStep.title}
          onContact={() =>
            handleChoice("confirm_contact", selectedReason, null)
          }
          onContinueCancellation={() =>
            handleChoice("confirm_cancellation", selectedReason, null)
          }
        />
      );

    case "user_stays":
      return (
        <UserStays
          title={currentStep.title}
          offeredDiscount={offeredDiscount}
          onClose={handleClose}
        />
      );

    case "confirm_cancellation":
      return (
        <ConfirmCancellation
          title={currentStep.title}
          onCancel={onCancellationCompleted}
          closeModal={handleClose}
        />
      );

    case "success_cancellation":
      return (
        <CancellationSuccess
          title={
            wasCancelledImmediately ?
              OVERDUE_CANCELLATION_SUCCESS_TITLE
            : currentStep.title
          }
          closeModal={handleClose}
        />
      );

    default:
      return null;
  }
}
